import React, { useMemo } from 'react';
import styled from '@emotion/native';
import { white } from '@/shared/styles/colors';
import { GALLERY_CONSTANTS } from '@/shared/styles/constants';
import {
  createResizedPhotoUrl,
  FULL_WIDTH,
  HALF_WIDTH,
  getGalleryImageHeight,
} from '@/shared/utils/photoUtils';

interface GalleryImageDisplayProps {
  landscapeImage?: string | null;
  portraitImages?: string[];
  imageContentWidth: number;
}

const GalleryImageDisplay: React.FC<GalleryImageDisplayProps> = ({
  landscapeImage,
  portraitImages = [],
  imageContentWidth,
}) => {
  // Filter out empty portrait images
  const visiblePortraitImages = portraitImages.filter(
    (img) => img && img.trim() !== '',
  );

  const landscapeHeight = getGalleryImageHeight('landscape');
  const portraitHeight = getGalleryImageHeight('portrait');

  const resizedLandscapeImage = useMemo(() => {
    return landscapeImage
      ? createResizedPhotoUrl(landscapeImage, FULL_WIDTH)
      : '';
  }, [landscapeImage]);

  const resizedPortraitImages = useMemo(() => {
    return visiblePortraitImages.map((imageUrl) =>
      createResizedPhotoUrl(imageUrl, HALF_WIDTH),
    );
  }, [visiblePortraitImages]);

  return (
    <GalleryGrid imageContentWidth={imageContentWidth}>
      {landscapeImage && (
        <LandscapeImageContainer height={landscapeHeight}>
          <GalleryImage source={{ uri: resizedLandscapeImage }} />
        </LandscapeImageContainer>
      )}

      {resizedPortraitImages.map((imageUrl, index) => (
        <PortraitImageContainer key={index} height={portraitHeight}>
          <GalleryImage source={{ uri: imageUrl }} />
        </PortraitImageContainer>
      ))}
    </GalleryGrid>
  );
};

export default GalleryImageDisplay;

const GalleryGrid = styled.View<{ imageContentWidth: number }>(
  ({ imageContentWidth }) => ({
    width: imageContentWidth,
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: GALLERY_CONSTANTS.COLUMN_GAP,
    rowGap: GALLERY_CONSTANTS.ROW_GAP,
    alignItems: 'center',
    maxWidth: GALLERY_CONSTANTS.MAX_WIDTH,
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 50,
  }),
);

const LandscapeImageContainer = styled.View<{ height: number }>(
  ({ height }) => ({
    flexBasis: GALLERY_CONSTANTS.LANDSCAPE_FLEX_BASIS,
    height,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: GALLERY_CONSTANTS.BORDER_RADIUS,
    overflow: 'hidden',
  }),
);

const PortraitImageContainer = styled.View<{ height: number }>(
  ({ height }) => ({
    flexBasis: GALLERY_CONSTANTS.PORTRAIT_FLEX_BASIS,
    height,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: GALLERY_CONSTANTS.BORDER_RADIUS,
    overflow: 'hidden',
  }),
);

const GalleryImage = styled.Image({
  width: '100%',
  height: '100%',
  borderRadius: GALLERY_CONSTANTS.BORDER_RADIUS,
});
